import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { ModalNext, ModalHeader, ModalBody, ModalFooter } from '@jutro/components';
import { withValidation, validationPropTypes, useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import metadata from './EditContactDetailsPopUp.metadata.json5';
import { AccountService } from 'gw-capability-gateway-policy';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { platformMessages } from 'ml-components-platform-react';

import styles from './EditContactDetailsPopUp.module.scss';
import messages from './EditContactDetailsPopup.messages';
import { Button } from '@jutro/legacy/components';

import { ScrollToError } from '@jutro/legacy/wizard-next';

const homePrimaryPhoneTypeCode = 'home';
const workPrimaryPhoneTypeCode = 'work';
const mobilePrimaryPhoneTypeCode = 'mobile';
const primaryPhoneTypePath = 'primaryPhoneType';
const primaryPhoneTypeValPath = 'primaryPhoneType.value';

const EditContactDetailsPopUp = (props) => {
    const {
        accountData,
        title,
        actionBtnLabel,
        cancelBtnLabel,
        isOpen,
        onResolve,
        onReject,
        viewModelService,
        id
    } = props;

    const {
        showProgressDialog,
        hideProgressDialog,
        showErrorModal
    } = MLModalService();

    const {
        clearErrorAlerts,
        addErrorAlert,
        getNumberOfAlerts,
        addWarningAlert,
        clearBlockAlerts
    } = MLNotificationBlockUtil();

    const translator = useTranslator();
    const [accountDetailsVM, setAccountDetailsVM] = useState(null);
    const [primaryPhone, setPrimaryPhone] = useState('cellNumber');
    const [loaded, setLoaded] = useState(false);
    const [showAlertMsg, setShowAlertMsg] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    
    const { onValidate, isComponentValid } = useValidation('EditContactDetailsPopUp');
    const { authHeader } = useAuthentication();
    const canEditContactFields = accountData.policySummaries.length ===0 || accountData.policySummaries.every(summary => {
        return summary.canEditCommunicationsFields_Ext;
    });

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const createVM = useCallback((model) => {
        return viewModelService.create(
            model,
            'pc',
            'edge.capabilities.policycommon.accountcontact.dto.AccountContactDTO'
        );
    }, [viewModelService]);

    const initialAccountData = useCallback((model) => {
        if (!_.isEmpty(accountData) && !_.isEmpty(accountData.accountHolder)) {
            _.set(model, 'value', accountData.accountHolder);
        }
        return model;
    }, [accountData]);

    useEffect(() => {
        const model = createVM({});
        const updatedModel = initialAccountData(model);
        let primaryPhoneType = '';
        switch (updatedModel.primaryPhoneType.value?.code) {
            case 'work':
                primaryPhoneType = 'workNumber';
                break;
            case 'home':
                primaryPhoneType = 'homeNumber';
                break;
            case 'mobile':
                primaryPhoneType = 'cellNumber';
                break;
            default:
                primaryPhoneType = primaryPhone;
        }

        if (updatedModel.subtype.value === 'Company' && primaryPhoneType === 'cellNumber') {
            primaryPhoneType = 'homeNumber';
        }
        updatedModel.value.originalEmailAddress = _.get(updatedModel, 'emailAddress1.value')
        updatedModel.value.originalHomeNumber = _.get(updatedModel, 'homeNumber.value')
        updatedModel.value.originalCellNumber = _.get(updatedModel, 'cellNumber.value')
        updatedModel.value.originalWorkNumber = _.get(updatedModel, 'workNumber.value')
        setAccountDetailsVM(updatedModel);
        setPrimaryPhone(primaryPhoneType);
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isHomePhonePrimary, updateIsHomePhonePrimary] = useState(false);
    const [isWorkPhonePrimary, updateIsWorkPhonePrimary] = useState(false);
    const [isCellPhonePrimary, updateIsCellPhonePrimary] = useState(false);

    const [addtlPhoneNumbersVisible, updateAddtlPhoneNumbersVisible] = useState(false);

    const writeValue = useCallback((value, path) => {
        if (accountDetailsVM && path) {
            const model = viewModelService.clone(accountDetailsVM);
            _.set(model, path, value);
            setAccountDetailsVM(model);
        }
    }, [accountDetailsVM, viewModelService]);

    const changePrimaryPhone = useCallback((primaryPhoneType, checked) => {
        let selectedPrimaryPhoneTypeCode;
        if (!checked) {
            selectedPrimaryPhoneTypeCode = undefined;
            writeValue(undefined, primaryPhoneTypePath);
        } else {
            const primaryPhoneTypes = viewModelService.productMetadata.get('pc').types.getTypelist('PrimaryPhoneType');
            const selectedPrimaryPhoneType = primaryPhoneTypes.getCode(primaryPhoneType);
            writeValue(primaryPhoneType, primaryPhoneTypePath);

            selectedPrimaryPhoneTypeCode = selectedPrimaryPhoneType.code;
        }
        updateIsHomePhonePrimary(selectedPrimaryPhoneTypeCode === homePrimaryPhoneTypeCode);
        updateIsWorkPhonePrimary(selectedPrimaryPhoneTypeCode === workPrimaryPhoneTypeCode);
        updateIsCellPhonePrimary(selectedPrimaryPhoneTypeCode === mobilePrimaryPhoneTypeCode);
    }, [viewModelService.productMetadata, writeValue]);

    const updatePhoneNumber = useCallback((phoneType, value, changedPath) => {
        let unmaskedPhoneNumber;
        if (value && changedPath) {
            unmaskedPhoneNumber = value.replace(/[^\d]/g, '');
            _.set(accountDetailsVM, `${changedPath}.value`, unmaskedPhoneNumber);
        }

        switch (phoneType) {
            case 'home':
                if (_.get(accountDetailsVM, 'homeNumber.value') === '') {
                    _.set(accountDetailsVM, 'homeNumber.value', undefined);
                }
                break;
            case 'cell':
                if (_.get(accountDetailsVM, 'cellNumber.value') === '') {
                    _.set(accountDetailsVM, 'cellNumber.value', undefined);
                }
                break;
            case 'work':
                if (_.get(accountDetailsVM, 'workNumber.value') === '') {
                    _.set(accountDetailsVM, 'workNumber.value', undefined);
                }
                break;
            default:
                break;
        }

        writeValue(unmaskedPhoneNumber, changedPath);
    }, [accountDetailsVM, writeValue]);

    useEffect(() => {
        if (loaded || !accountDetailsVM) {
            return ;
        }
        const primaryPhoneType = _.get(accountDetailsVM, primaryPhoneTypeValPath);
        if (primaryPhoneType !== undefined) {
            if (_.get(accountDetailsVM, 'cellNumber.value') || _.get(accountDetailsVM, 'workNumber.value')) {
                updateAddtlPhoneNumbersVisible(true);
            }
            changePrimaryPhone(_.get(accountDetailsVM, primaryPhoneTypeValPath).code, true);
        } else {
            changePrimaryPhone(undefined, false);
        }
        setLoaded(true);
    }, [accountDetailsVM, changePrimaryPhone, loaded]);

    const showAdditionalPhoneNumbers = () => {
        updateAddtlPhoneNumbersVisible(true);
    };

    const handleSave = useCallback(() => {
        if (!isComponentValid) {
            setShowValidationErrors(true);
        } else if (accountDetailsVM.primaryPhoneType.value === undefined) {
            setErrorTimestamp(errorTimestamp + 1);
            setShowAlertMsg(true);
        } else {
            const requestObject = {
                accountHolder: accountDetailsVM.value,
                accountNumber: accountData.accountNumber
            };
            setShowAlertMsg(false);
            onResolve(requestObject);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentValid, accountDetailsVM, onResolve]);

    const disableContactField = (value) => {
        return (!canEditContactFields && value);
    };

    const validateEmailAddress = (accountContactDetailsVm) => {
        const account = accountContactDetailsVm;
        let emailAddress1 =  _.get(account, 'emailAddress1.value');
        if(emailAddress1){
            emailAddress1 = emailAddress1.trim();
        }
        let isValidEmailEntred =  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)
        if(isValidEmailEntred){
            const checkEmailVerified_Ext = AccountService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            );
            showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
            checkEmailVerified_Ext.then((res) => {
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log("Email verification Api Called");
                if(res == true){
                    _.set(account, 'emailVerified_Ext.value', true);
                    _.set(account, 'emailAddress1.value', emailAddress1);
                }
                else{
                    _.set(account, 'emailVerified_Ext.value', false);
                    _.set(account, 'emailAddress1.value', emailAddress1 + " ");
                    setShowValidationErrors(true);
                }
            }).catch((e) => {
                clearBlockAlerts('invalidEmail');
                addWarningAlert(translator(messages.invalidEmail), 'invalidEmail');
                _.set(account, 'emailVerified_Ext.value', false);
                _.set(account, 'emailAddress1.value', emailAddress1);
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log(e);
                window.scrollTo(0, 0);
            });
        }
        
    };

    const emailTrim = ( event ) => {
        let emailAddress1 =  event.target.value;
        if(emailAddress1 !== "" && emailAddress1 !== undefined){
            emailAddress1 = emailAddress1.trim();
            event.target.value = emailAddress1
        }
        let email = _.get(accountDetailsVM, 'emailAddress1.value'); 
        if( email !== "" && email !== undefined){
            email = email.trim();
            _.set(accountDetailsVM, 'emailAddress1.value', email);
        }
    }

    const handleOnBlur = () => {
        // validate email
        validateEmailAddress(accountDetailsVM);
    }

    const overrideProps = {
        homePhonePrimaryCheckbox: {
            value: isHomePhonePrimary,
            onValueChange: (value) => changePrimaryPhone(homePrimaryPhoneTypeCode, value)
        },
        homePhoneNumber: {
            onValueChange: (value, changedPath) => updatePhoneNumber('home', value, changedPath),
            disabled: (accountDetailsVM) ? disableContactField(accountDetailsVM.value.originalHomeNumber) : false
        },
        additionalPhoneNumberLinkContainer: {
            visible: !addtlPhoneNumbersVisible
        },
        additionalPhoneNumbersLink: {
            onClick: showAdditionalPhoneNumbers
        },
        additionalPhoneNumbersContainer: {
            visible: addtlPhoneNumbersVisible
        },
        workPhonePrimaryCheckbox: {
            value: isWorkPhonePrimary,
            onValueChange: (value) => changePrimaryPhone(workPrimaryPhoneTypeCode, value)
        },
        workPhoneNumber: {
            onValueChange: (value, changedPath) => updatePhoneNumber('work', value, changedPath),
            disabled: (accountDetailsVM) ? disableContactField(accountDetailsVM.value.originalWorkNumber) : false
        },
        cellPhonePrimaryCheckbox: {
            value: isCellPhonePrimary,
            onValueChange: (value) => changePrimaryPhone(mobilePrimaryPhoneTypeCode, value)
        },
        cellPhoneNumber: {
            onValueChange: (value, changedPath) => updatePhoneNumber('cell', value, changedPath),
            disabled: (accountDetailsVM) ? disableContactField(accountDetailsVM.value.originalWorkNumber) : false
        },
        primaryPhoneAlert: {
            visible: showAlertMsg,
            message: translator(messages.primaryPhoneAlertMsg)
        },
        emailAddress: {
            onBlur: handleOnBlur,
            onFocus: (event) => emailTrim(event),
            showValidationErrors: showValidationErrors,
            disabled: (accountDetailsVM) ? disableContactField(accountDetailsVM.value.originalEmailAddress) : false
        }
    };

    return (
        <ModalNext isOpen={isOpen} className={styles.editContactDetailsModal}>
            <ModalHeader
                title={title}
                onClose={onReject}
            />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={accountDetailsVM}
                    overrideProps={overrideProps}
                    classNameMap={styles}
                    showErrors={showValidationErrors}
                    onValueChange={writeValue}
                    onValidationChange={onValidate}
                />
                <ScrollToError counter={errorTimestamp} />
            </ModalBody>
            <ModalFooter>
                <Button onClick={onReject} type="outlined">
                    {cancelBtnLabel}
                </Button>
                <Button onClick={handleSave}>
                    {actionBtnLabel}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
};

EditContactDetailsPopUp.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func.isRequired,
        clone: PropTypes.func.isRequired,
    }).isRequired,
    id: PropTypes.string.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    accountData: PropTypes.shape({
        accountNumber: PropTypes.string.isRequired,
        accountHolder: PropTypes.shape({
            primaryAddress: PropTypes.shape({}).isRequired,
            emailAddress1: PropTypes.shape({}).isRequired,
            subtype: PropTypes.shape({}).isRequired,
            cellNumber: PropTypes.string.isRequired,
            homeNumber: PropTypes.string.isRequired,
            workNumber: PropTypes.string.isRequired,
            primaryPhoneType: PropTypes.string
        }).isRequired
    }).isRequired,
    title: PropTypes.string.isRequired,
    actionBtnLabel: PropTypes.shape({}).isRequired,
    cancelBtnLabel: PropTypes.shape({}).isRequired,
    onReject: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onResolve: PropTypes.func.isRequired,
    ...validationPropTypes,
};

export default withValidation(EditContactDetailsPopUp);
